import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MapPin, Eye, Heart, UserCog, Phone, Sparkles, MessageCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LoveTchat } from "@/components/LoveTchat";
import { LoveSwipe, REACTIONS_LOVE as REACTIONS, photoLove } from "@/components/LoveSwipe";
import { LoveMatchAnimation } from "@/components/LoveMatchAnimation";
import { LoveAdmirateurs } from "@/components/LoveAdmirateurs";
import { getUser } from "@/lib/user";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoveCard = ({ profil, onReagir, onPartagerNumero, onDiscuter, index, moiId }) => {
  const [numero, setNumero] = useState(profil.numero_partage || null);
  const photo = photoLove(profil, moiId);
  const voirNumero = async () => {
    try {
      const r = await axios.get(`${API}/love/numero-recu/${moiId}/${profil.id}`);
      setNumero(r.data.numero);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Aucun numéro partagé.");
    }
  };
  return (
    <motion.article
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: (index % 8) * 0.05 }}
      data-testid={`love-card-${profil.id}`}
      className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-stone-900/5 transition-shadow hover:shadow-xl"
    >
      <div className="relative h-72 bg-gradient-to-br from-stone-200 to-stone-300">
        <span className="absolute inset-0 flex items-center justify-center font-heading text-6xl font-extrabold text-white/70">
          {(profil.firstname || "?").charAt(0)}
        </span>
        <img src={photo} alt={profil.firstname} loading="lazy"
          className="relative h-full w-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-4">
          <h3 className="font-heading text-xl font-extrabold text-white">
            {profil.firstname}{profil.age ? `, ${profil.age}` : ""}
          </h3>
          <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-white/80">
            <MapPin size={11} /> {profil.quartier || profil.commune || profil.city || "Côte d'Ivoire"}
            {profil.taille ? ` · ${profil.taille}` : ""}
            {profil.silhouette ? ` · ${profil.silhouette}` : ""}
          </p>
        </div>
        {profil.photo_visibilite !== "visible" && (
          <span data-testid={`love-anonymat-${profil.id}`}
            className="absolute right-3 top-3 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm">
            {profil.photo_visibilite === "anonyme" ? "Anonyme" : "Photo floutée"}
          </span>
        )}
      </div>

      <div className="space-y-3 p-4">
        {profil.recherche && (
          <p className="inline-flex items-center gap-1.5 rounded-full bg-[#FF4757]/10 px-3 py-1 text-xs font-bold text-[#FF4757]">
            <Sparkles size={12} /> {profil.recherche}
          </p>
        )}
        {profil.bio && <p className="text-sm leading-relaxed text-stone-600 line-clamp-3">{profil.bio}</p>}
        {profil.interets?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {profil.interets.map((i) => (
              <span key={i} className="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-semibold text-stone-600">
                {i}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between text-[11px] font-semibold text-stone-400">
          <span className="inline-flex items-center gap-1"><Eye size={12} /> {profil.vues} vues</span>
          <span className="inline-flex items-center gap-1"><Heart size={12} /> {profil.affinites} affinités</span>
        </div>

        <div className="flex items-center justify-between gap-1.5 border-t border-stone-100 pt-3">
          {REACTIONS.map(({ cle, label, Icone, couleur }) => (
            <button key={cle} title={label} onClick={() => onReagir(profil, cle)}
              data-testid={`love-reaction-${cle}-${profil.id}`}
              className="flex flex-1 flex-col items-center gap-1 rounded-2xl py-2 transition-transform hover:scale-110 active:scale-95"
              style={{
                backgroundColor: profil.ma_reaction === cle ? `${couleur}1a` : "transparent",
                color: profil.ma_reaction === cle ? couleur : "#78716C",
              }}>
              <Icone size={20} />
              <span className="text-[10px] font-bold">{label}</span>
            </button>
          ))}
        </div>

        {onPartagerNumero && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => onDiscuter(profil)} data-testid={`love-discuter-${profil.id}`}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF4757] px-4 py-2.5 text-xs font-bold text-white transition-transform hover:scale-[1.02]">
              <MessageCircle size={14} /> Discuter
            </button>
            <button onClick={() => onPartagerNumero(profil)} data-testid={`love-partager-numero-${profil.id}`}
              className="flex-1 rounded-full bg-[#2F363F] px-4 py-2.5 text-xs font-bold text-white transition-transform hover:scale-[1.02]">
              Je partage mon numéro
            </button>
            {numero ? (
              <a href={`tel:${numero}`} data-testid={`love-numero-${profil.id}`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#FF4757] px-4 py-2.5 text-xs font-bold text-white">
                <Phone size={13} /> {numero}
              </a>
            ) : (
              <button onClick={voirNumero} data-testid={`love-voir-numero-${profil.id}`}
                className="rounded-full border border-stone-200 px-4 py-2.5 text-xs font-bold text-stone-600 hover:border-stone-900">
                Son numéro
              </button>
            )}
          </div>
        )}
      </div>
    </motion.article>
  );
};

/** FodindinLove : fil de profils, réactions à 4 niveaux et matchs. */
export default function LoveFeed() {
  const moi = getUser();
  const [params] = useSearchParams();
  const [onglet, setOnglet] = useState(
    ["fil", "matchs", "admirateurs"].includes(params.get("onglet")) ? params.get("onglet") : "fil",
  );
  const [profils, setProfils] = useState(null);
  const [matchs, setMatchs] = useState([]);
  const [recherche, setRecherche] = useState("");
  const [refs, setRefs] = useState(null);
  const [tchat, setTchat] = useState(null);
  const [matchAnim, setMatchAnim] = useState(null);
  const [admirateurs, setAdmirateurs] = useState(null);

  useSeo({
    titre: "FodindinLove — rencontres sérieuses en Côte d'Ivoire",
    description:
      "Profils Love détaillés (taille, silhouette, centres d'intérêt, recherche), réactions " +
      "coucou / j'aime / coup de cœur / ça chauffe, photo anonyme ou floutée et partage du " +
      "numéro uniquement entre personnes qui se sont aimées.",
    chemin: "/love",
  });

  const rafraichirMatchs = useCallback(() => {
    if (!moi) return;
    axios.get(`${API}/love/matchs/${moi.id}`).then((r) => setMatchs(r.data)).catch(() => {});
    axios.get(`${API}/love/mes-admirateurs/${moi.id}`)
      .then((r) => setAdmirateurs(r.data)).catch(() => {});
  }, [moi?.id]);

  // Anti-chevauchement : seule la réponse de la DERNIÈRE requête est retenue. Deux
  // chargements concurrents faisaient remplacer la pile affichée par une autre liste
  // (le membre voyait un profil inconnu apparaître à la place du sien).
  const requeteEnCours = useRef(0);

  const charger = useCallback(() => {
    const jeton = ++requeteEnCours.current;
    axios
      .get(`${API}/love/profils`, { params: { viewer_id: moi?.id, recherche: recherche || undefined } })
      .then((r) => { if (jeton === requeteEnCours.current) setProfils(r.data); })
      .catch(() => { if (jeton === requeteEnCours.current) setProfils([]); });
    rafraichirMatchs();
  }, [moi?.id, recherche, rafraichirMatchs]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { axios.get(`${API}/love/referentiel`).then((r) => setRefs(r.data)).catch(() => {}); }, []);

  /** Réaction depuis la pile : la carte avance toute seule, on ne recharge donc pas
   *  le fil (cela ferait sauter la pile) — seuls les matchs sont rafraîchis. */
  const reagir = async (profil, type) => {
    if (!moi) return toast.error("Créez votre profil pour réagir.");
    try {
      const r = await axios.post(`${API}/love/reaction`, {
        auteur_id: moi.id, cible_id: profil.id, type,
      });
      if (r.data.match) setMatchAnim(r.data.cible || profil);
      else toast.success(r.data.message);
      rafraichirMatchs();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Réaction impossible.");
    }
  };

  const passer = async (profil) => {
    if (!moi) return toast.error("Créez votre profil pour découvrir les profils.");
    await axios.post(`${API}/love/passer`, { auteur_id: moi.id, cible_id: profil.id }).catch(() => {});
  };

  const partagerNumero = async (profil) => {
    try {
      const r = await axios.post(`${API}/love/partager-numero`, {
        auteur_id: moi.id, destinataire_id: profil.id,
      });
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Partage impossible.");
    }
  };

  const liste = onglet === "fil" ? profils : matchs;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-6 lg:px-8" data-testid="page-love-feed">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#FF4757]">
              FodindinLove
            </span>
            <h1 className="mt-2 font-heading text-4xl font-extrabold text-[#2F363F] sm:text-5xl">
              Trouvez votre match
            </h1>
            <p className="mt-3 max-w-xl text-base text-stone-600">
              Des profils pensés pour la rencontre : silhouette, centres d'intérêt, ce que
              la personne cherche. Votre numéro ne sort jamais sans votre accord.
            </p>
          </div>
          <Link to="/love/mon-profil" data-testid="love-editer-profil"
            className="inline-flex items-center gap-2 rounded-full bg-[#FF4757] px-5 py-3 text-sm font-bold text-white transition-transform hover:scale-[1.03]">
            <UserCog size={16} /> Mon profil Love
          </Link>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {[["fil", "Découvrir"],
            ["matchs", `Mes matchs${matchs.length ? ` (${matchs.length})` : ""}`],
            ["admirateurs", `Qui m'a aimé${admirateurs?.total ? ` (${admirateurs.total})` : ""}`]].map(
            ([cle, label]) => (
              <button key={cle} onClick={() => setOnglet(cle)} data-testid={`love-onglet-${cle}`}
                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                  onglet === cle ? "bg-[#2F363F] text-white" : "bg-white text-stone-600 ring-1 ring-stone-900/5"
                }`}>
                {label}
              </button>
            ),
          )}
          {onglet === "fil" && refs && (
            <select value={recherche} onChange={(e) => setRecherche(e.target.value)}
              data-testid="love-filtre-recherche"
              className="rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-stone-600 ring-1 ring-stone-900/5">
              <option value="">Toutes les intentions</option>
              {refs.recherches.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
        </div>

        <AnimatePresence mode="wait">
          {liste === null ? (
            <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#FF4757]" size={34} /></div>
          ) : onglet === "admirateurs" ? (
            <LoveAdmirateurs donnees={admirateurs} moiId={moi?.id}
              onDiscuter={setTchat} CarteProfil={LoveCard} />
          ) : onglet === "fil" ? (
            liste.length === 0 ? (
              <p className="py-24 text-center text-stone-500" data-testid="love-vide">
                Aucun nouveau profil Love à découvrir pour le moment.
              </p>
            ) : (
              <LoveSwipe profils={liste} moiId={moi?.id} onReagir={reagir} onPasser={passer} />
            )
          ) : liste.length === 0 ? (
            <p className="py-24 text-center text-stone-500" data-testid="love-vide">
              Pas encore de match : réagissez aux profils qui vous plaisent.
            </p>
          ) : (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
              data-testid="love-grille">
              {liste.map((p, i) => (
                <LoveCard key={p.id} profil={p} index={i} moiId={moi?.id} onReagir={reagir}
                  onDiscuter={setTchat}
                  onPartagerNumero={moi ? partagerNumero : null} />
              ))}
            </div>
          )}
        </AnimatePresence>
      </main>
      <AnimatePresence>
        {matchAnim && (
          <LoveMatchAnimation moi={moi} profil={matchAnim}
            onContinuer={() => setMatchAnim(null)}
            onDiscuter={() => { setTchat(matchAnim); setMatchAnim(null); }} />
        )}
        {tchat && moi && (
          <LoveTchat moiId={moi.id} profil={tchat} onFermer={() => setTchat(null)} />
        )}
      </AnimatePresence>
      <Footer />
    </div>
  );
}
